import React, { useEffect, useRef } from 'react';

/**
 * VortexBackground
 * Apple-Glass Aura background: drifting radial blobs over a static grid+dots
 * texture. Heavily optimised:
 *   - The static grid + dots are rendered ONCE to an offscreen canvas
 *     and blitted each frame (massive perf win vs. re-drawing every frame).
 *   - Frame rate is capped at ~30fps (the animation is intentionally slow,
 *     so 60fps is wasted CPU/GPU).
 *   - When the tab/page is hidden, the loop pauses.
 */
export default function VortexBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Same gotcha as FireParticles: the canvas only exists after the
    // portal target has been set, so wait until canvasRef has a node.
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    let animationFrameId;
    let isVisible = !document.hidden;
    let last = 0;
    const TARGET_FPS = 30;
    const FRAME_MS = 1000 / TARGET_FPS;

    // Offscreen canvas for the static grid + dot texture
    const texture = document.createElement('canvas');
    const tctx = texture.getContext('2d');

    const buildTexture = () => {
      texture.width = canvas.width;
      texture.height = canvas.height;
      const isMobile = canvas.width < 768;
      const spacing = isMobile ? 60 : 120;
      const offsetX = (canvas.width % spacing) / 2;
      const offsetY = (canvas.height % spacing) / 2;

      tctx.clearRect(0, 0, texture.width, texture.height);
      // Grid lines
      tctx.strokeStyle = 'rgba(255, 255, 255, 0.015)';
      tctx.lineWidth = 1;
      for (let x = offsetX; x < canvas.width; x += spacing) {
        tctx.beginPath();
        tctx.moveTo(x, 0);
        tctx.lineTo(x, canvas.height);
        tctx.stroke();
      }
      for (let y = offsetY; y < canvas.height; y += spacing) {
        tctx.beginPath();
        tctx.moveTo(0, y);
        tctx.lineTo(canvas.width, y);
        tctx.stroke();
      }
      // Dots (3x3 desktop / 2x2 mobile)
      tctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
      const dotsPerSide = isMobile ? 2 : 3;
      const step = spacing / dotsPerSide;
      const dotOffset = step / 2;
      for (let x = offsetX + dotOffset; x < canvas.width; x += step) {
        for (let y = offsetY + dotOffset; y < canvas.height; y += step) {
          tctx.beginPath();
          tctx.arc(x, y, 1, 0, Math.PI * 2);
          tctx.fill();
        }
      }
    };

    const resize = () => {
      // Drive size off the canvas's own getBoundingClientRect — that
      // tracks the CSS layout (100vw / 100lvh) instead of window.innerHeight,
      // so iOS Safari's address-bar show/hide no longer feeds height jitter
      // into the canvas. Same buffer-size guard against tiny diffs stays
      // for the rare cases that DO change (rotation, real resize).
      const rect = canvas.getBoundingClientRect();
      // Cold-start guard: on the very first paint the canvas may not be
      // laid out yet, so getBoundingClientRect returns 0×0. Building a
      // 0-size offscreen texture and drawImage-ing it throws ("image
      // argument is a canvas with width or height of 0") and crashes the
      // whole app via the ErrorBoundary. Fall back to the window size,
      // and if even that's 0, bail and let the next frame retry.
      const newW = Math.round(rect.width) || window.innerWidth || 0;
      const newH = Math.round(rect.height) || window.innerHeight || 0;
      if (newW === 0 || newH === 0) return;
      const sameWidth = newW === canvas.width;
      const heightDiff = Math.abs(newH - canvas.height);
      if (sameWidth && heightDiff < 150) return;
      // Belt-and-suspenders: a canvas op throwing here used to bubble to
      // the ErrorBoundary and take down the ENTIRE site. The background is
      // purely decorative, so swallow any draw error — worst case is one
      // un-painted frame, never a crashed page.
      try {
        canvas.width = newW;
        canvas.height = newH;
        buildTexture();
        // Repaint immediately so the cleared canvas is filled before the
        // next browser composite, avoiding the black flash.
        ctx.fillStyle = '#060608';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (texture.width > 0 && texture.height > 0) {
          ctx.drawImage(texture, 0, 0);
        }
      } catch (e) {
        /* decorative background — never crash the app over a paint */
      }
    };
    resize();
    window.addEventListener('resize', resize);
    // Orientation flips on iOS can miss the plain 'resize' event.
    window.visualViewport?.addEventListener('resize', resize);

    // Subtle, edge-positioned blobs — like the blue one on bottom-right of the
    // reference: centers are at the edges so only the soft glow tail is
    // visible inside the viewport, no defined circular shape.
    const colors = [
      'rgba(255, 110, 40, 0.16)',
      'rgba(120, 60, 240, 0.13)',
      'rgba(240, 50, 90, 0.11)',
    ];
    const colorsMobile = [
      'rgba(255, 110, 40, 0.24)',
      'rgba(120, 60, 240, 0.20)',
      'rgba(240, 50, 90, 0.16)',
    ];
    const isMobileScreen = () => canvas.width < 768;
    const activeColors = isMobileScreen() ? colorsMobile : colors;

    // Anchor each blob at edges so the visible part is only the soft falloff.
    const positions = [
      { x: 0.05, y: 0.00 },  // top-left edge
      { x: 1.05, y: 1.00 },  // bottom-right edge
      { x: 1.05, y: 0.20 },  // right edge top
    ];
    const velocities = [
      { vx: 0.04, vy: 0.03 },
      { vx: -0.03, vy: -0.04 },
      { vx: -0.04, vy: 0.03 },
    ];
    // Large radii so only the soft outer falloff bleeds into the viewport.
    const radii = [550, 600, 480];

    const blobs = activeColors.map((color, i) => ({
      x: positions[i].x * canvas.width,
      y: positions[i].y * canvas.height,
      radius: radii[i],
      targetRadius: radii[i],
      vx: velocities[i].vx,
      vy: velocities[i].vy,
      color,
    }));

    let time = 0;

    const render = (now) => {
      animationFrameId = requestAnimationFrame(render);
      if (!isVisible) return;
      if (now - last < FRAME_MS) return;
      last = now;

      time += 0.0008;

      ctx.fillStyle = '#060608';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      blobs.forEach((blob, idx) => {
        blob.x += blob.vx;
        blob.y += blob.vy;
        // Bounds account for off-screen anchored blobs (radius * 1.2)
        const bx = blob.radius * 1.2;
        if (blob.x < -bx || blob.x > canvas.width + bx) blob.vx *= -1;
        if (blob.y < -bx || blob.y > canvas.height + bx) blob.vy *= -1;
        const pulsatingRadius = blob.targetRadius + Math.sin(time * 2.0 + idx) * 45;

        // Simple 3-stop gradient (Apple-Glass style). The mid-stop @ 0.5 with
        // 30% alpha creates a soft falloff that doesn't band on OLED displays.
        const c = blob.color;
        const alpha = parseFloat(c.match(/[\d.]+\)$/)[0]);
        const baseRgba = c.replace(/[\d.]+\)$/, '');

        const gradient = ctx.createRadialGradient(
          blob.x, blob.y, 10,
          blob.x, blob.y, pulsatingRadius
        );
        gradient.addColorStop(0, c);
        gradient.addColorStop(0.5, `${baseRgba}${(alpha * 0.3).toFixed(3)})`);
        gradient.addColorStop(1, `${baseRgba}0)`);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(blob.x, blob.y, pulsatingRadius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Single blit of the pre-rendered grid+dots texture. Guard the
      // 0-size case so a not-yet-built texture can't throw every frame.
      if (texture.width > 0 && texture.height > 0) {
        ctx.drawImage(texture, 0, 0);
      }
    };

    const onVisibility = () => {
      isVisible = !document.hidden;
    };
    document.addEventListener('visibilitychange', onVisibility);

    animationFrameId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resize);
      window.visualViewport?.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      data-testid="vortex-background"
      style={{
        zIndex: 0,
        width: '100vw',
        height: '100lvh',
        transform: 'translate3d(0, 0, 0)',
        WebkitTransform: 'translate3d(0, 0, 0)',
        WebkitBackfaceVisibility: 'hidden',
      }}
    />
  );
}
